interface HeroProps {
  onCTAClick: () => void;
}

export default function Hero({ onCTAClick }: HeroProps) {
  return (
    <section className="relative bg-gradient-to-br from-slate-50 via-white to-teal-50/60 overflow-hidden pt-16">
      {/* Background photo - solar panel work, blended with color */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url('https://static.readdy.ai/image/a44b33c2a19c260b8697c4ae9afddc14/01dc72a0c49b1dfd62ad19ca91f6ec02.png')",
        }}
      />
      {/* Overlay: white-teal gradient to blend photo with brand colors */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/85 via-white/75 to-teal-50/80" />

      {/* Decorative background shapes */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-teal-100/50 rounded-full blur-[120px] pointer-events-none -translate-y-1/3 translate-x-1/3" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-emerald-100/60 rounded-full blur-[100px] pointer-events-none translate-y-1/3 -translate-x-1/3" />

      {/* Subtle dot pattern */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: 'radial-gradient(circle, #0d9488 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />

      <div className="relative z-10 w-full max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-28">
        <div className="flex flex-col items-center text-center">

          {/* Left: Text content */}
          <div className="flex flex-col items-center text-center">
            {/* Badge */}
            <div className="mb-4 md:mb-6">
              <span className="inline-flex items-center gap-1.5 md:gap-2 border border-teal-300 text-teal-700 bg-teal-50 px-3 py-1.5 md:px-5 md:py-2 rounded-full text-[10px] md:text-xs font-semibold tracking-widest uppercase shadow-sm">
                <i className="ri-sun-line text-teal-500 text-xs md:text-sm"></i>
                工務店様向け 太陽光発電 販売代行サービス
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-extrabold leading-tight mb-4 md:mb-6">
              <span className="block text-2xl md:text-5xl lg:text-5xl tracking-tight mb-1 md:mb-2 text-slate-800">
                太陽光発電の販売・施工を
              </span>
              <span className="block text-2xl md:text-5xl lg:text-5xl tracking-tight text-slate-800">
                <span className="text-teal-500">すべて</span>、ライズコーポレーションが代行します。
              </span>
            </h1>

            {/* Sub copy */}
            <p className="text-slate-500 text-sm md:text-lg max-w-xl mb-6 md:mb-10 leading-relaxed px-2">
              商談・設計・補助金申請・施工まで一括対応。<br className="hidden md:block" />
              工務店様はご紹介いただくだけで、安定した収益を実現できます。
            </p>

            {/* Stats row */}
            <div className="flex flex-wrap justify-center gap-6 md:gap-10 mb-8 md:mb-12">
              {[
                { value: '500+', label: '提携工務店実績' },
                { value: '98%', label: '顧客満足度' },
                { value: '0円', label: '初期費用・リスク' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl md:text-4xl font-extrabold text-teal-500 leading-none">
                    {stat.value}
                  </div>
                  <div className="text-slate-400 text-[10px] md:text-xs mt-1 tracking-wide">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center w-full px-2">
              <button
                onClick={onCTAClick}
                className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 md:px-10 md:py-4 rounded-md text-sm md:text-base font-bold transition-all shadow-md hover:shadow-teal-300/50 hover:scale-105 whitespace-nowrap cursor-pointer w-full sm:w-auto"
              >
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-mail-send-line"></i>
                  無料お見積り・提携のご相談
                </span>
              </button>

              <a
                href="tel:0120-710-056"
                className="border border-slate-300 hover:border-teal-400 text-slate-700 hover:text-teal-600 bg-white hover:bg-teal-50 px-6 py-3 md:px-10 md:py-4 rounded-md text-sm md:text-base font-bold transition-all shadow-sm whitespace-nowrap cursor-pointer w-full sm:w-auto text-center"
              >
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-phone-line"></i>
                  0120-710-056
                </span>
              </a>
            </div>

            <p className="text-slate-400 text-[10px] md:text-xs mt-4 md:mt-5 flex items-center justify-center gap-1.5">
              <i className="ri-time-line"></i>
              受付時間：平日 9:00〜18:00　／　お見積りは完全無料
            </p>
          </div>

        </div>
      </div>
    </section>
  );
}